import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { states, districts, taxpayerTypes } from '../data/mockData';
import PartBRegistration from '../components/registration/PartBRegistration';
import MockInboxLight from '../components/MockInboxLight';
import GSTPortalDashboard from '../components/GSTPortalDashboard';
import GSTPortalWrapper from '../components/GSTPortalWrapper';
import { 
  AlertCircle, CheckCircle2, Mail, Info, ArrowRight, Inbox, ArrowLeft,
  Menu, X, User, LogOut, Home, Smartphone, Bell, Star, ChevronDown, ChevronRight
} from 'lucide-react';
import {
  generateTRN15,
  generateTRNEmail,
  addToMockInbox,
  getMockInbox,
  saveRegistrationSession
} from '../utils/registrationUtils';
import { trackActivity } from '../lib/activity';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Collapsible Knowledge Accordion Component
const KnowledgeAccordion = ({ title, icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="mb-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-gray-300 hover:bg-white/10 rounded-lg transition-colors"
      >
        <span className="text-sm">{icon}</span>
        <span className="flex-1 text-sm font-medium">{title}</span>
        {isOpen ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500" />
        )}
      </button>
      {isOpen && (
        <div className="mt-1 ml-2 p-3 bg-white/5 rounded-lg border-l-2 border-teal-500/50">
          {children}
        </div>
      )}
    </div>
  );
};

const GSTRegistration = () => {
  const navigate = useNavigate();

  // Track simulator start (logged-in only, fire-and-forget)
  useEffect(() => {
    trackActivity({
      simulator_id: 'gst_registration',
      simulator_name: 'GST Registration Practice',
      action: 'started',
    });
  }, []);

  const [registrationType, setRegistrationType] = useState('new');
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [showTRNSuccessModal, setShowTRNSuccessModal] = useState(false);
  const [showPartB, setShowPartB] = useState(false);
  const [generatedTRN, setGeneratedTRN] = useState('');
  const [mobileOtp, setMobileOtp] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [trnInput, setTrnInput] = useState('');
  const [captchaText] = useState('ABCD12');
  const [captchaInput, setCaptchaInput] = useState('');
  const [trnOtpModal, setTrnOtpModal] = useState(false);
  const [trnOtpValue, setTrnOtpValue] = useState('');
  const [showMockInbox, setShowMockInbox] = useState(false);
  const [unreadEmails, setUnreadEmails] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showTRNLogin, setShowTRNLogin] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showTaxpayerTooltip, setShowTaxpayerTooltip] = useState(false);
  const [showStateTooltip, setShowStateTooltip] = useState(false);
  const [showLegalNameTooltip, setShowLegalNameTooltip] = useState(false);
  const [taxpayerTooltipDismissed, setTaxpayerTooltipDismissed] = useState(false);
  const [showTRNLoginTooltip, setShowTRNLoginTooltip] = useState(false);
  const [trnEmailViewed, setTrnEmailViewed] = useState(false);
  const [showPortalDashboard, setShowPortalDashboard] = useState(false);
  const [trnCreationDate, setTrnCreationDate] = useState(null);
  const [partBActiveSection, setPartBActiveSection] = useState(1);
  const [partBCompletedSections, setPartBCompletedSections] = useState([]);
  
  // Feedback modal state
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackLiked, setFeedbackLiked] = useState('');
  const [feedbackImprove, setFeedbackImprove] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  
  // OTP codes for verification
  const MOBILE_OTP = '123456';
  const EMAIL_OTP = '654321';

  // Get lead info from localStorage
  const leadEmail = localStorage.getItem('practiq_lead_email') || '';
  const leadName = localStorage.getItem('practiq_lead_name') || 'User';

  // Update unread count
  useEffect(() => {
    const updateUnreadCount = () => {
      const inbox = getMockInbox();
      setUnreadEmails(inbox.filter(e => !e.isRead).length);
    };
    updateUnreadCount();
    const interval = setInterval(updateUnreadCount, 1000);
    return () => clearInterval(interval);
  }, [showMockInbox, showOTPModal]);

  const [formData, setFormData] = useState({
    taxpayerType: '',
    state: '',
    district: '',
    legalName: '',
    pan: '',
    email: leadEmail,
    mobile: ''
  });

  useEffect(() => {
    if (leadEmail && !formData.email) {
      setFormData(prev => ({ ...prev, email: leadEmail }));
    }
  }, [leadEmail]);

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.taxpayerType) newErrors.taxpayerType = 'Please select taxpayer type';
    if (!formData.state) newErrors.state = 'Please select state';
    if (!formData.district) newErrors.district = 'Please select district';
    if (!formData.legalName) newErrors.legalName = 'Please enter legal name';
    if (!formData.pan || formData.pan.length !== 10) newErrors.pan = 'Please enter valid 10-digit PAN';
    if (!formData.email) newErrors.email = 'Please enter email';
    if (!formData.mobile || formData.mobile.length !== 10) newErrors.mobile = 'Please enter valid 10-digit mobile';
    if (captchaInput !== captchaText) newErrors.captcha = 'Invalid captcha';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceed = () => {
    if (validateForm()) {
      // Add OTP email to mock inbox
      const otpEmail = {
        id: `email_otp_${Date.now()}`,
        subject: 'GST Verification OTP',
        from: 'donotreply@demogstpractiq.in',
        to: formData.email,
        date: new Date().toISOString(),
        isRead: false,
        body: `
Dear Taxpayer,

Your One Time Password (OTP) for GST Registration verification is:

═══════════════════════════════════════════════════
                    EMAIL OTP
═══════════════════════════════════════════════════

                     ${EMAIL_OTP}

═══════════════════════════════════════════════════

This OTP is valid for 10 minutes.

Please do not share this OTP with anyone.

Regards,
PractiQ
`
      };
      addToMockInbox(otpEmail);
      
      // Show notification
      setShowNotification(true);
      setTimeout(() => {
        setShowNotification(false);
        setShowOTPModal(true);
      }, 2000);
    }
  };

  const handleOTPVerify = () => {
    if (mobileOtp !== MOBILE_OTP) {
      setOtpError('Invalid Mobile OTP. Check the mobile screen for correct OTP.');
      return;
    }
    
    if (emailOtp !== EMAIL_OTP) {
      setOtpError('Invalid Email OTP. Check your Mock Inbox for correct OTP.');
      return;
    }

    setIsVerifying(true);
    setOtpError('');

    setTimeout(() => {
      setIsVerifying(false);
      setShowOTPModal(false);
      
      const trn = generateTRN15();
      setGeneratedTRN(trn);
      
      const email = generateTRNEmail(trn, formData.email, formData.legalName);
      addToMockInbox(email);
      
      saveRegistrationSession({
        trn,
        partAData: formData,
        status: 'part_a_complete'
      });
      
      // Track Part-A completion (TRN generated)
      trackActivity({
        simulator_id: 'gst_registration',
        simulator_name: 'GST Registration Practice',
        action: 'saved',
        metadata: { stage: 'part_a_complete', trn },
      });

      setShowTRNSuccessModal(true);
    }, 1500);
  };

  const handleTRNVerify = () => {
    if (!trnInput) {
      alert('Please enter TRN');
      return;
    }
    setTrnOtpModal(true);
  };

  const TRN_OTP = '245980';
  
  const handleTRNOtpVerify = () => {
    if (trnOtpValue === TRN_OTP) {
      setTrnOtpModal(false);
      setGeneratedTRN(trnInput);
      setShowTRNLogin(false);
      setTrnCreationDate(new Date());
      setShowPortalDashboard(true); // Show GST Portal Dashboard
    }
  };

  const handleEditApplication = () => {
    setShowPortalDashboard(false);
    setShowPartB(true);
  };

  const handlePortalLogout = () => {
    setShowPortalDashboard(false);
    setRegistrationType('new');
    setTrnInput('');
    setGeneratedTRN('');
  };

  const handleProceedToPartB = () => {
    setShowTRNSuccessModal(false);
    setShowTRNLogin(true);
    setTrnInput(generatedTRN);
    setRegistrationType('trn');
  };

  // Feedback submission handler
  const handleFeedbackSubmit = async () => {
    if (feedbackRating === 0) {
      alert('Please select a rating');
      return;
    }

    setFeedbackSubmitting(true);
    const userEmail = localStorage.getItem('practiq_lead_email') || localStorage.getItem('pw_batch_email');

    try {
      await fetch(`${API_URL}/api/leads/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
          rating: feedbackRating,
          feedback_liked: feedbackLiked,
          feedback_improve: feedbackImprove
        })
      });

      localStorage.setItem('pw_feedback_submitted', 'true');
      setShowFeedback(false);
      alert('Thank you for your feedback! 🙏');
      
      // Proceed with logout/navigation
      localStorage.removeItem('practiq_lead_email');
      localStorage.removeItem('practiq_lead_name');
      localStorage.removeItem('practiq_lead_firstName');
      localStorage.removeItem('practiq_registration_session');
      localStorage.removeItem('practiq_mock_inbox');
      navigate('/gst');

    } catch (err) {
      console.error('Feedback error:', err);
      alert('Could not submit feedback. Please try again.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const handleLogout = () => {
    // Check if user came from PW batch and hasn't given feedback
    const isPWUser = localStorage.getItem('pw_batch_email');
    const hasFeedback = localStorage.getItem('pw_feedback_submitted');
    
    if (isPWUser && !hasFeedback) {
      setShowFeedback(true);
      return;
    }
    
    localStorage.removeItem('practiq_lead_email');
    localStorage.removeItem('practiq_lead_name');
    localStorage.removeItem('practiq_lead_firstName');
    localStorage.removeItem('practiq_registration_session');
    localStorage.removeItem('practiq_mock_inbox');
    navigate('/gst');
  };

  // Render Part B with GST Portal Wrapper
  if (showPartB) {
    return (
      <GSTPortalWrapper 
        onLogout={handlePortalLogout}
        creationDate={trnCreationDate}
        activeSection={partBActiveSection}
        completedSections={partBCompletedSections}
        onSectionClick={(sectionId) => {
          // Only allow clicking on completed sections or the next section
          if (partBCompletedSections.includes(sectionId) || sectionId === partBActiveSection || sectionId === partBActiveSection + 1) {
            setPartBActiveSection(sectionId);
          }
        }}
      >
        <PartBRegistration 
          trn={generatedTRN}
          onBack={() => {
            setShowPartB(false);
            setShowPortalDashboard(true);
          }}
          onNewRegistration={() => {
            setShowPartB(false);
            setShowPortalDashboard(false);
            setFormData({
              taxpayerType: '', state: '', district: '', legalName: '',
              pan: '', email: leadEmail, mobile: ''
            });
            setGeneratedTRN('');
            setRegistrationType('new');
            setCaptchaInput('');
            setPartBActiveSection(1);
            setPartBCompletedSections([]);
          }}
          partAData={formData}
          hideHeader={true}
          activeSection={partBActiveSection}
          onActiveSectionChange={setPartBActiveSection}
          completedSections={partBCompletedSections}
          onCompletedSectionsChange={setPartBCompletedSections}
        />
      </GSTPortalWrapper>
    );
  }

  // Show GST Portal Dashboard after TRN login
  if (showPortalDashboard) {
    return (
      <GSTPortalDashboard 
        trn={generatedTRN}
        creationDate={trnCreationDate}
        onEditApplication={handleEditApplication}
        onLogout={handlePortalLogout}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Fixed Sidebar - Higher z-index to stay above OTP overlay */}
      <div className={`fixed inset-y-0 left-0 z-[90] w-64 bg-[#0D1B2A] transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-white font-bold text-lg">PractiQ</span>
              <button onClick={() => setSidebarOpen(false)} className="text-white hover:bg-white/10 p-1 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            <button
              onClick={() => navigate('/gst')}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-white/10 rounded-lg transition-colors"
            >
              <Home className="w-5 h-5" />
              <span>GST Dashboard</span>
            </button>
            
            <button
              onClick={() => setShowMockInbox(true)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all relative ${
                showOTPModal 
                  ? 'bg-blue-500/20 text-blue-300 ring-2 ring-blue-400/50 animate-pulse' 
                  : 'text-gray-300 hover:bg-white/10'
              }`}
              data-testid="sidebar-mock-inbox"
            >
              <Inbox className={`w-5 h-5 ${showOTPModal ? 'text-blue-400' : ''}`} />
              <span>Mock Inbox</span>
              {unreadEmails > 0 && (
                <span className={`absolute right-4 min-w-[22px] h-[22px] px-1 text-white text-xs rounded-full flex items-center justify-center font-medium ${
                  showOTPModal ? 'bg-blue-500 animate-bounce' : 'bg-red-500'
                }`}>
                  {unreadEmails} {showOTPModal && 'New'}
                </span>
              )}
            </button>

            {/* Knowledge Base Section */}
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-gray-500 text-xs uppercase tracking-wider mb-3 px-2">Knowledge Base</p>
              
              {/* 1. GST Turnover Limits - Collapsible */}
              <KnowledgeAccordion 
                title="GST Turnover Limits" 
                icon="📊"
                defaultOpen={false}
              >
                <div className="space-y-3">
                  <div>
                    <p className="text-teal-400 text-xs font-semibold mb-1">For Goods</p>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Normal States</span>
                        <span className="text-white">₹40 Lakhs</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Special Category</span>
                        <span className="text-white">₹20 Lakhs</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-cyan-400 text-xs font-semibold mb-1">For Services</p>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Normal States</span>
                        <span className="text-white">₹20 Lakhs</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Special Category</span>
                        <span className="text-white">₹10 Lakhs</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-amber-400 text-xs font-semibold mb-1">Special Category States</p>
                    <p className="text-gray-400 text-xs leading-relaxed">
                      Arunachal Pradesh, Manipur, Meghalaya, Mizoram, Nagaland, Puducherry, Sikkim, Tripura, Uttarakhand
                    </p>
                  </div>
                </div>
              </KnowledgeAccordion>

              {/* 2. Documents Required - Collapsible */}
              <KnowledgeAccordion 
                title="Documents Required" 
                icon="📄"
                defaultOpen={false}
              >
                <div className="text-xs space-y-3">
                  {/* Proprietorship */}
                  <div>
                    <p className="text-teal-400 font-semibold mb-1.5">👤 Proprietorship</p>
                    <div className="text-gray-300 space-y-1 ml-1">
                      <p>• Photo of Proprietor <span className="text-gray-500">(JPG, 100KB)</span></p>
                      <p>• Photo of Auth. Signatory <span className="text-gray-500">(JPG, 100KB)</span></p>
                      <p>• Letter of Authorization <span className="text-gray-500">(PDF, 100KB)</span></p>
                      <p>• Address Proof <span className="text-gray-500">(Any one)</span></p>
                      <p className="text-gray-500 text-[10px] ml-2">Electricity Bill / Consent Letter / Property Tax / Municipal Khata</p>
                    </div>
                  </div>

                  {/* Private Limited Company */}
                  <div className="pt-2 border-t border-white/10">
                    <p className="text-cyan-400 font-semibold mb-1.5">🏢 Private Limited Company</p>
                    <div className="text-gray-300 space-y-1 ml-1">
                      <p>• Certificate of Incorporation <span className="text-gray-500">(PDF, 1MB)</span></p>
                      <p>• Photo of Directors <span className="text-gray-500">(JPG, 100KB)</span></p>
                      <p>• Photo of Auth. Signatory <span className="text-gray-500">(JPG, 100KB)</span></p>
                      <p>• Board Resolution + Acceptance <span className="text-gray-500">(PDF, 100KB)</span></p>
                      <p>• Address Proof <span className="text-gray-500">(Any one)</span></p>
                      <p className="text-gray-500 text-[10px] ml-2">Electricity Bill / Rent Agreement / Property Tax / NOC</p>
                    </div>
                  </div>

                  {/* File Size Note */}
                  <div className="pt-2 border-t border-white/10">
                    <p className="text-amber-400 text-[10px]">📌 Max Size: Photos 100KB, Docs 500KB-2MB</p>
                  </div>
                </div>
              </KnowledgeAccordion>

              {/* 3. Registration Timeline - Collapsible */}
              <KnowledgeAccordion 
                title="Registration Timeline" 
                icon="⏱️"
                defaultOpen={false}
              >
                <div className="text-xs text-gray-300 space-y-1.5">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Part-A (TRN)</span>
                    <span className="text-teal-400">Instant</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Part-B Submission</span>
                    <span className="text-teal-400">15 days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">GSTIN Issuance</span>
                    <span className="text-teal-400">3-7 days</span>
                  </div>
                </div>
              </KnowledgeAccordion>

              {/* 4. Important Tips - Collapsible */}
              <KnowledgeAccordion 
                title="Important Tips" 
                icon="💡"
                defaultOpen={false}
              >
                <div className="text-xs text-gray-300 space-y-1.5">
                  <p>• Keep PAN & Aadhaar linked</p>
                  <p>• Use registered mobile/email</p>
                  <p>• Complete Part-B within 15 days</p>
                  <p>• Keep TRN safe for future login</p>
                </div>
              </KnowledgeAccordion>
            </div>
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{leadName}</p>
                <p className="text-gray-400 text-xs truncate">{leadEmail}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-4 py-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors text-sm"
            >
              <LogOut className="w-4 h-4" />
              Exit Simulator
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-0'}`}>
        {/* Top Header */}
        <header className="bg-[#0D1B2A] text-white py-3 px-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-4">
            {!sidebarOpen && (
              <button 
                onClick={() => setSidebarOpen(true)}
                className="p-2 hover:bg-white/10 rounded-lg"
              >
                <Menu className="w-5 h-5" />
              </button>
            )}
            <div>
              <h1 className="text-lg font-semibold">New Registration</h1>
              <p className="text-xs text-gray-400">Part A - Basic Details</p>
            </div>
          </div>
        </header>

        {/* Form Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          <Card className="max-w-2xl mx-auto shadow-lg">
            <CardContent className="p-6">
              {/* Registration Type Selection - Single Line */}
              <div className="flex items-center justify-between gap-4 mb-6 flex-nowrap">
                <RadioGroup 
                  value={registrationType} 
                  onValueChange={(v) => {
                    setRegistrationType(v);
                    if (v === 'trn') {
                      setShowTRNLoginTooltip(false);
                    }
                  }}
                  className="flex items-center gap-4 flex-nowrap"
                >
                  <div className="flex items-center gap-1.5 whitespace-nowrap">
                    <RadioGroupItem value="new" id="new" />
                    <Label htmlFor="new" className="cursor-pointer text-sm whitespace-nowrap">New Registration</Label>
                  </div>
                  <div className="flex items-center gap-1.5 whitespace-nowrap relative">
                    <RadioGroupItem value="trn" id="trn" />
                    <Label htmlFor="trn" className="cursor-pointer text-sm whitespace-nowrap">Temporary Reference Number (TRN)</Label>
                    
                    {/* TRN Login Tooltip - Shows after inbox close */}
                    {showTRNLoginTooltip && (
                      <div className="absolute left-0 top-full mt-2 w-64 z-50 animate-in slide-in-from-top-2 duration-300">
                        {/* Arrow pointing up */}
                        <div className="absolute left-8 -top-2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 border-b-blue-600"></div>
                        <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl p-4 shadow-xl">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">👆</span>
                              <span className="font-semibold text-sm">Login here for Part-B!</span>
                            </div>
                            <button 
                              onClick={() => setShowTRNLoginTooltip(false)}
                              className="text-white/70 hover:text-white p-1 hover:bg-white/10 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-xs text-blue-100 mt-2">
                            Click this option, paste your TRN, and continue to Part-B! 🚀
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </RadioGroup>
                <span className="text-red-500 text-xs whitespace-nowrap flex-shrink-0">* indicates mandatory fields</span>
              </div>

              {registrationType === 'new' ? (
                /* New Registration Form - Single Column Layout */
                <div className="space-y-4">
                  <div className="relative">
                    <Label className="text-sm font-medium">I am a <span className="text-red-500">*</span></Label>
                    <Select 
                      value={formData.taxpayerType} 
                      onValueChange={(v) => {
                        if (v === 'Taxpayer') {
                          handleInputChange('taxpayerType', v);
                          setShowTaxpayerTooltip(true);
                          setShowLegalNameTooltip(false);
                        }
                      }}
                      onOpenChange={(open) => {
                        if (open) {
                          setShowLegalNameTooltip(false);
                        }
                      }}
                    >
                      <SelectTrigger className={`mt-1 ${errors.taxpayerType ? 'border-red-500' : ''}`}>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {taxpayerTypes.map(type => (
                          <SelectItem 
                            key={type} 
                            value={type}
                            disabled={type !== 'Taxpayer'}
                            className={type !== 'Taxpayer' ? 'text-gray-400 cursor-not-allowed opacity-50' : ''}
                          >
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.taxpayerType && <p className="text-red-500 text-xs mt-1">{errors.taxpayerType}</p>}
                    
                    {/* Educational Tooltip */}
                    {showTaxpayerTooltip && (
                      <div className="absolute left-full top-0 ml-4 w-72 z-50 animate-in slide-in-from-left-2 duration-300">
                        {/* Arrow pointing left */}
                        <div className="absolute left-0 top-8 -ml-2 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-blue-600"></div>
                        <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl p-4 shadow-xl">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                                <Info className="w-4 h-4" />
                              </div>
                              <span className="font-semibold text-sm">Why Taxpayer?</span>
                            </div>
                            <button 
                              onClick={() => {
                                setShowTaxpayerTooltip(false);
                                setTaxpayerTooltipDismissed(true);
                              }}
                              className="text-white/70 hover:text-white p-1 hover:bg-white/10 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-sm text-blue-100 leading-relaxed">
                            <strong>Taxpayer</strong> is the most common registration type — used by most businesses selling goods or services.
                          </p>
                          <p className="text-xs text-blue-200 mt-2">
                            Other categories like Tax Deductor, GST Practitioner, etc. are for specific purposes!
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* State and District Section with Tooltip */}
                  <div className="relative">
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium">State / UT <span className="text-red-500">*</span></Label>
                        <Select 
                          value={formData.state} 
                          onValueChange={(v) => handleInputChange('state', v)}
                          onOpenChange={(open) => {
                            if (open) {
                              // Auto-hide other tooltips
                              if (!taxpayerTooltipDismissed) {
                                setShowTaxpayerTooltip(false);
                              }
                              setShowLegalNameTooltip(false);
                              setShowStateTooltip(true);
                            }
                          }}
                        >
                          <SelectTrigger className={`mt-1 ${errors.state ? 'border-red-500' : ''}`}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {states.map(state => (
                              <SelectItem key={state.code} value={state.code}>{state.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                      </div>

                      <div>
                        <Label className="text-sm font-medium">District <span className="text-red-500">*</span></Label>
                        <Select 
                          value={formData.district} 
                          onValueChange={(v) => handleInputChange('district', v)}
                          disabled={!formData.state}
                          onOpenChange={(open) => {
                            if (open) {
                              setShowLegalNameTooltip(false);
                            }
                          }}
                        >
                          <SelectTrigger className={`mt-1 ${errors.district ? 'border-red-500' : ''}`}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {(districts[formData.state] || []).map(district => (
                              <SelectItem key={district} value={district}>{district}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.district && <p className="text-red-500 text-xs mt-1">{errors.district}</p>}
                      </div>
                    </div>
                    
                    {/* State/District Educational Tooltip */}
                    {showStateTooltip && (
                      <div className="absolute left-full top-0 ml-4 w-80 z-50 animate-in slide-in-from-left-2 duration-300">
                        {/* Arrow pointing left */}
                        <div className="absolute left-0 top-10 -ml-2 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-blue-600"></div>
                        <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl p-4 shadow-xl">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                                <Info className="w-4 h-4" />
                              </div>
                              <span className="font-semibold text-sm">Simulation Mode</span>
                            </div>
                            <button 
                              onClick={() => setShowStateTooltip(false)}
                              className="text-white/70 hover:text-white p-1 hover:bg-white/10 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-sm text-blue-100 leading-relaxed">
                            Since this is a <strong>simulation tool</strong>, you can select any State and District for practice.
                          </p>
                          <div className="mt-3 p-2.5 bg-white/10 rounded-lg">
                            <p className="text-xs text-blue-100">
                              💡 <strong>Example:</strong> If you live in Delhi, select "Delhi" and then choose any district like "Central Delhi".
                            </p>
                          </div>
                          <p className="text-xs text-blue-200 mt-3">
                            Can't find your exact district? No worries! Select any district — this is for learning purposes only.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Legal Name & PAN Section with Tooltip */}
                  <div className="relative">
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium">Legal Name of Business <span className="text-red-500">*</span></Label>
                        <Input
                          value={formData.legalName}
                          onChange={(e) => handleInputChange('legalName', e.target.value)}
                          placeholder="As mentioned in PAN"
                          className={`mt-1 ${errors.legalName ? 'border-red-500' : ''}`}
                          onFocus={() => {
                            setShowStateTooltip(false);
                            setShowLegalNameTooltip(true);
                          }}
                        />
                        {errors.legalName && <p className="text-red-500 text-xs mt-1">{errors.legalName}</p>}
                      </div>

                      <div>
                        <Label className="text-sm font-medium">Permanent Account Number (PAN) <span className="text-red-500">*</span></Label>
                        <Input
                          value={formData.pan}
                          onChange={(e) => handleInputChange('pan', e.target.value.toUpperCase())}
                          placeholder="ABCDE1234F"
                          maxLength={10}
                          className={`mt-1 uppercase ${errors.pan ? 'border-red-500' : ''}`}
                          onFocus={() => {
                            setShowStateTooltip(false);
                            setShowLegalNameTooltip(true);
                          }}
                        />
                        {errors.pan && <p className="text-red-500 text-xs mt-1">{errors.pan}</p>}
                      </div>
                    </div>
                    
                    {/* Legal Name & PAN Educational Tooltip */}
                    {showLegalNameTooltip && (
                      <div className="absolute left-full top-0 ml-4 w-80 z-50 animate-in slide-in-from-left-2 duration-300">
                        {/* Arrow pointing left */}
                        <div className="absolute left-0 top-8 -ml-2 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-blue-600"></div>
                        <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl p-4 shadow-xl">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                                <Info className="w-4 h-4" />
                              </div>
                              <span className="font-semibold text-sm">What's Legal Name? 🤔</span>
                            </div>
                            <button 
                              onClick={() => setShowLegalNameTooltip(false)}
                              className="text-white/70 hover:text-white p-1 hover:bg-white/10 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          
                          <p className="text-sm text-blue-100 leading-relaxed mb-3">
                            It's simple! The <strong>name on your PAN card</strong> is your Legal Name. Trading name comes later.
                          </p>
                          
                          <div className="p-2.5 bg-white/10 rounded-lg mb-3">
                            <p className="text-xs text-blue-100 mb-2">
                              💡 <strong>Let's say:</strong>
                            </p>
                            <p className="text-xs text-blue-100">
                              Shivam Shukla wants to start a shop called <em>"Shivam Enterprises"</em>
                            </p>
                            <p className="text-xs text-white mt-2 font-medium">
                              ✅ Legal Name = <strong>SHIVAM SHUKLA</strong>
                            </p>
                            <p className="text-[10px] text-blue-200 mt-1">
                              (Shop name will be added in Part-B!)
                            </p>
                          </div>
                          
                          <div className="p-2 bg-green-500/20 rounded-lg border border-green-400/30">
                            <p className="text-xs text-green-100">
                              🎯 Go ahead, use your real name & PAN — or make one up! 😄
                            </p>
                            <p className="text-[10px] text-green-200 mt-1">
                              🗑️ Relax! Everything disappears once you're done practicing. Poof! ✨
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Email Address <span className="text-red-500">*</span></Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="your.email@example.com"
                      className={`mt-1 ${errors.email ? 'border-red-500' : ''}`}
                      onFocus={() => setShowLegalNameTooltip(false)}
                    />
                    <p className="text-xs text-gray-500 mt-1">OTP will be sent to this email address</p>
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Mobile Number <span className="text-red-500">*</span></Label>
                    <div className="flex gap-2 mt-1">
                      <span className="flex items-center px-3 bg-gray-100 border rounded-md text-sm">+91</span>
                      <Input
                        value={formData.mobile}
                        onChange={(e) => handleInputChange('mobile', e.target.value.replace(/\D/g, ''))}
                        placeholder="9876543210"
                        maxLength={10}
                        className={`flex-1 ${errors.mobile ? 'border-red-500' : ''}`}
                        onFocus={() => setShowLegalNameTooltip(false)}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Separate OTP will be sent on this Mobile Number</p>
                    {errors.mobile && <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>}
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Enter Captcha <span className="text-red-500">*</span></Label>
                    <div className="flex gap-2 mt-1">
                      <div className="px-4 py-2 bg-gray-200 rounded font-mono text-lg tracking-widest select-none">
                        {captchaText}
                      </div>
                      <Input
                        value={captchaInput}
                        onChange={(e) => setCaptchaInput(e.target.value.toUpperCase())}
                        placeholder="Enter captcha"
                        maxLength={6}
                        className={`flex-1 uppercase ${errors.captcha ? 'border-red-500' : ''}`}
                        onFocus={() => setShowLegalNameTooltip(false)}
                      />
                    </div>
                    {errors.captcha && <p className="text-red-500 text-xs mt-1">{errors.captcha}</p>}
                  </div>

                  {/* Info Banner */}
                  <Alert className="bg-blue-50 border-blue-200">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800 text-sm">
                      <strong>Simulation Note:</strong> After proceeding, check your Mock Inbox and Mobile for OTP codes.
                    </AlertDescription>
                  </Alert>

                  {/* Proceed Button */}
                  <Button
                    onClick={handleProceed}
                    className="w-full bg-[#0D1B2A] hover:bg-[#1B2838] h-11"
                  >
                    PROCEED
                  </Button>
                </div>
              ) : (
                /* TRN Login Form */
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Temporary Reference Number (TRN) <span className="text-red-500">*</span></Label>
                    <Input
                      value={trnInput}
                      onChange={(e) => setTrnInput(e.target.value)}
                      placeholder="Enter your 15-digit TRN"
                      maxLength={15}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Enter Captcha <span className="text-red-500">*</span></Label>
                    <div className="flex gap-2 mt-1">
                      <div className="px-4 py-2 bg-gray-200 rounded font-mono text-lg tracking-widest select-none">
                        {captchaText}
                      </div>
                      <Input
                        value={captchaInput}
                        onChange={(e) => setCaptchaInput(e.target.value.toUpperCase())}
                        placeholder="Enter captcha"
                        maxLength={6}
                        className="flex-1 uppercase"
                      />
                    </div>
                  </div>

                  <Button onClick={handleTRNVerify} className="w-full bg-[#0D1B2A] hover:bg-[#1B2838]">
                    PROCEED
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>

      {/* Notification Toast */}
      {/* Notification Toast - Highlighted for Mock Inbox */}
      {showNotification && (
        <div className="fixed top-4 right-4 z-[100] animate-in slide-in-from-right duration-300">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 flex items-center gap-3 max-w-sm">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">1 New Email Received!</p>
              <p className="text-sm text-gray-500">Open <span className="text-blue-600 font-medium">Mock Inbox</span> to view OTP</p>
            </div>
          </div>
        </div>
      )}

      {/* Immersive OTP Verification Overlay */}
      {showOTPModal && (
        <>
          {/* Blurred Background Overlay */}
          <div 
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm transition-all duration-300"
            onClick={() => setShowOTPModal(false)}
          />
          
          {/* Central OTP Modal */}
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto animate-in zoom-in-95 fade-in duration-300"
              style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)' }}
              data-testid="otp-modal"
            >
              {/* Modal Header */}
              <div className="relative px-6 pt-6 pb-4 border-b border-gray-100">
                <button
                  onClick={() => setShowOTPModal(false)}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                  data-testid="otp-modal-close"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="text-center">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Mail className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">OTP Verification</h2>
                  <p className="text-sm text-gray-500 mt-1">Enter the OTPs to verify your identity</p>
                </div>
              </div>

              {/* OTP Input Fields */}
              <div className="px-6 py-6 space-y-5">
                <div>
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-green-500" />
                    Mobile OTP
                  </Label>
                  <Input
                    value={mobileOtp}
                    onChange={(e) => {
                      setMobileOtp(e.target.value.replace(/\D/g, ''));
                      setOtpError('');
                    }}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className="mt-2 text-center text-2xl font-mono tracking-[0.5em] h-14 border-2 focus:border-green-500"
                    data-testid="mobile-otp-input"
                  />
                  <p className="text-xs text-gray-400 mt-1.5">Check the mobile notification on your screen</p>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-blue-500" />
                    Email OTP
                  </Label>
                  <Input
                    value={emailOtp}
                    onChange={(e) => {
                      setEmailOtp(e.target.value.replace(/\D/g, ''));
                      setOtpError('');
                    }}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className="mt-2 text-center text-2xl font-mono tracking-[0.5em] h-14 border-2 focus:border-blue-500"
                    data-testid="email-otp-input"
                  />
                  <p className="text-xs text-gray-400 mt-1.5">
                    Open <span className="text-blue-600 font-medium">Mock Inbox</span> from sidebar to find your email OTP
                  </p>
                </div>
                
                {otpError && (
                  <Alert variant="destructive" className="animate-in shake duration-300">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{otpError}</AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Verify Button */}
              <div className="px-6 pb-6">
                <Button
                  onClick={handleOTPVerify}
                  disabled={isVerifying || mobileOtp.length !== 6 || emailOtp.length !== 6}
                  className="w-full bg-gradient-to-r from-[#0D1B2A] to-[#1B2838] hover:from-[#1B2838] hover:to-[#2C3E50] h-12 text-base font-medium shadow-lg"
                  data-testid="verify-proceed-btn"
                >
                  {isVerifying ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Verifying...
                    </span>
                  ) : (
                    <>
                      VERIFY & PROCEED
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Sliding Mobile Phone from Bottom Right */}
          <div 
            className="fixed bottom-4 right-4 z-[80] animate-in slide-in-from-bottom-full duration-700 ease-out"
            style={{ animationDelay: '200ms' }}
          >
            <div className="relative">
              {/* Phone Frame */}
              <div className="w-48 h-[340px] bg-gray-900 rounded-[2.5rem] p-1.5 shadow-2xl">
                {/* Phone Screen */}
                <div className="w-full h-full bg-gradient-to-b from-slate-800 to-slate-900 rounded-[2rem] overflow-hidden relative">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-gray-900 rounded-b-xl"></div>
                  
                  {/* Status Bar */}
                  <div className="flex justify-between items-center px-5 pt-7 text-white text-[10px]">
                    <span className="font-medium">9:41</span>
                    <div className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
                      </svg>
                      <span>5G</span>
                      <div className="w-5 h-2.5 border border-white rounded-sm ml-1">
                        <div className="w-3.5 h-full bg-green-400 rounded-sm"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* SMS Notification Card */}
                  <div className="mt-8 mx-2.5 animate-in slide-in-from-top duration-500" style={{ animationDelay: '500ms' }}>
                    <div className="bg-white rounded-2xl p-3.5 shadow-xl">
                      <div className="flex items-center gap-2.5 mb-2.5">
                        <div className="w-9 h-9 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-md">
                          <Smartphone className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 text-xs">GST Portal</p>
                          <p className="text-[10px] text-gray-500">now</p>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                        <p className="text-[11px] text-center text-green-600 font-semibold mb-1">MOBILE OTP</p>
                        <p className="text-[11px] text-gray-600 mb-2 text-center">Your OTP for GST Registration:</p>
                        <p className="text-2xl font-mono font-bold text-green-600 tracking-widest text-center">
                          {MOBILE_OTP}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-2 text-center">Valid for 10 mins. Do not share.</p>
                      </div>
                    </div>
                  </div>

                  {/* Home Indicator */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-24 h-1 bg-white/30 rounded-full"></div>
                </div>
              </div>
              
              {/* Phone Glow Effect */}
              <div className="absolute -inset-2 bg-gradient-to-t from-green-500/20 to-transparent rounded-[3rem] -z-10 blur-xl"></div>
            </div>
          </div>
        </>
      )}

      {/* TRN Success Modal - GST Portal Style */}
      <Dialog open={showTRNSuccessModal} onOpenChange={setShowTRNSuccessModal}>
        <DialogContent className="sm:max-w-lg">
          <div className="py-2">
            {/* Green Success Message Box - Exact GST Portal Style */}
            <div className="bg-green-100 border border-green-300 rounded-md p-4 mb-4">
              <p className="text-green-800 text-sm">
                You have successfully submitted Part A of the registration process. Your Temporary Reference Number (TRN) is <strong>{generatedTRN}</strong>.
              </p>
            </div>

            {/* Instructions with 15 days deadline */}
            <div className="text-gray-700 text-sm mb-6 leading-relaxed">
              <p>
                Using this TRN you can access the application from My saved Applications and submit on GST Portal. Part B of the application form needs to be completed within 15 days, i.e. by '<strong>{(() => {
                  const deadline = new Date();
                  deadline.setDate(deadline.getDate() + 15);
                  return deadline.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
                })()}</strong>' using this TRN.
              </p>
            </div>

            {/* Mock Inbox Alert */}
            <Alert className="bg-blue-50 border-blue-200 text-left mb-4">
              <Mail className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800 text-sm">
                <strong>Check your Mock Inbox</strong> - Click the Inbox in the sidebar to view your official TRN email.
              </AlertDescription>
            </Alert>

            {/* Proceed Button */}
            <Button onClick={handleProceedToPartB} className="w-full bg-[#0D1B2A] hover:bg-[#1B2838]">
              PROCEED
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* TRN OTP Modal with Mobile Phone Animation */}
      {trnOtpModal && (
        <>
          {/* Blurred Background Overlay */}
          <div 
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm transition-all duration-300"
            onClick={() => setTrnOtpModal(false)}
          />
          
          {/* Central TRN OTP Modal */}
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto animate-in zoom-in-95 fade-in duration-300"
              style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)' }}
              data-testid="trn-otp-modal"
            >
              {/* Modal Header */}
              <div className="relative px-6 pt-6 pb-4 border-b border-gray-100">
                <button
                  onClick={() => setTrnOtpModal(false)}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="text-center">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Smartphone className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">Verify TRN</h2>
                  <p className="text-sm text-gray-500 mt-1">Enter OTP to access Part-B registration</p>
                </div>
              </div>

              {/* OTP Input */}
              <div className="px-6 py-6 space-y-5">
                <div>
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-green-500" />
                    Mobile OTP
                  </Label>
                  <Input
                    value={trnOtpValue}
                    onChange={(e) => setTrnOtpValue(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className="mt-2 text-center text-2xl font-mono tracking-[0.5em] h-14 border-2 focus:border-green-500"
                    data-testid="trn-otp-input"
                  />
                  <p className="text-xs text-gray-400 mt-1.5">Check the mobile notification on your screen</p>
                </div>
              </div>

              {/* Verify Button */}
              <div className="px-6 pb-6">
                <Button
                  onClick={handleTRNOtpVerify}
                  disabled={trnOtpValue.length !== 6}
                  className="w-full bg-gradient-to-r from-[#0D1B2A] to-[#1B2838] hover:from-[#1B2838] hover:to-[#2C3E50] h-12 text-base font-medium shadow-lg"
                  data-testid="trn-verify-btn"
                >
                  VERIFY & PROCEED TO PART-B
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          {/* Sliding Mobile Phone from Bottom Right - TRN OTP */}
          <div 
            className="fixed bottom-4 right-4 z-[80] animate-in slide-in-from-bottom-full duration-700 ease-out"
            style={{ animationDelay: '200ms' }}
          >
            <div className="relative">
              {/* Phone Frame */}
              <div className="w-48 h-[340px] bg-gray-900 rounded-[2.5rem] p-1.5 shadow-2xl">
                {/* Phone Screen */}
                <div className="w-full h-full bg-gradient-to-b from-slate-800 to-slate-900 rounded-[2rem] overflow-hidden relative">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-gray-900 rounded-b-xl"></div>
                  
                  {/* Status Bar */}
                  <div className="flex justify-between items-center px-5 pt-7 text-white text-[10px]">
                    <span className="font-medium">9:41</span>
                    <div className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
                      </svg>
                      <span>5G</span>
                      <div className="w-5 h-2.5 border border-white rounded-sm ml-1">
                        <div className="w-3.5 h-full bg-green-400 rounded-sm"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* SMS Notification Card */}
                  <div className="mt-8 mx-2.5 animate-in slide-in-from-top duration-500" style={{ animationDelay: '500ms' }}>
                    <div className="bg-white rounded-2xl p-3.5 shadow-xl">
                      <div className="flex items-center gap-2.5 mb-2.5">
                        <div className="w-9 h-9 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-md">
                          <Smartphone className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 text-xs">GST Portal</p>
                          <p className="text-[10px] text-gray-500">now</p>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                        <p className="text-[11px] text-center text-green-600 font-semibold mb-1">TRN VERIFICATION OTP</p>
                        <p className="text-[11px] text-gray-600 mb-2 text-center">Your OTP for Part-B access:</p>
                        <p className="text-2xl font-mono font-bold text-green-600 tracking-widest text-center">
                          245980
                        </p>
                        <p className="text-[10px] text-gray-400 mt-2 text-center">Valid for 10 mins. Do not share.</p>
                      </div>
                    </div>
                  </div>

                  {/* Home Indicator */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-24 h-1 bg-white/30 rounded-full"></div>
                </div>
              </div>
              
              {/* Phone Glow Effect */}
              <div className="absolute -inset-2 bg-gradient-to-t from-green-500/20 to-transparent rounded-[3rem] -z-10 blur-xl"></div>
            </div>
          </div>
        </>
      )}

      {/* Mock Inbox Modal - Light Theme */}
      <MockInboxLight 
        isOpen={showMockInbox} 
        onClose={() => setShowMockInbox(false)} 
        onTRNEmailViewed={() => setTrnEmailViewed(true)}
        onInboxCloseAfterTRN={() => {
          if (trnEmailViewed) {
            setShowTRNLoginTooltip(true);
          }
        }}
      />

      {/* Feedback Modal */}
      <Dialog open={showFeedback} onOpenChange={(open) => {
        if (!open && feedbackRating === 0) {
          alert('Please share your feedback before leaving!');
          return;
        }
        setShowFeedback(open);
      }}>
        <DialogContent className="sm:max-w-lg bg-[#1E293B] border-white/10" data-testid="feedback-modal">
          <DialogHeader>
            <div className="w-14 h-14 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Star className="w-7 h-7 text-white" />
            </div>
            <DialogTitle className="text-xl text-white text-center">
              How was your experience?
            </DialogTitle>
            <DialogDescription className="text-gray-400 text-sm text-center mt-2">
              Your feedback helps us improve the learning experience
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Star Rating */}
            <div className="space-y-3">
              <Label className="text-gray-300 text-sm font-medium block text-center">Rate your experience *</Label>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setFeedbackRating(star)}
                    className="p-2 hover:scale-110 transition-transform"
                    data-testid={`star-rating-${star}`}
                  >
                    <Star
                      className={`w-10 h-10 transition-colors ${
                        star <= feedbackRating
                          ? 'text-amber-400 fill-amber-400'
                          : 'text-gray-600 hover:text-gray-500'
                      }`}
                    />
                  </button>
                ))}
              </div>
              {feedbackRating > 0 && (
                <p className="text-center text-amber-400 text-sm">
                  {feedbackRating === 5 ? 'Excellent! 🎉' : 
                   feedbackRating === 4 ? 'Great! 👍' : 
                   feedbackRating === 3 ? 'Good 😊' : 
                   feedbackRating === 2 ? 'Fair 🤔' : 'Needs Work 😕'}
                </p>
              )}
            </div>

            {/* What you liked */}
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm font-medium">What did you like? (Optional)</Label>
              <Textarea
                value={feedbackLiked}
                onChange={(e) => setFeedbackLiked(e.target.value)}
                placeholder="E.g., Real portal experience, step-by-step guidance..."
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 min-h-[80px]"
                data-testid="feedback-liked"
              />
            </div>

            {/* What can improve */}
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm font-medium">What can we improve? (Optional)</Label>
              <Textarea
                value={feedbackImprove}
                onChange={(e) => setFeedbackImprove(e.target.value)}
                placeholder="E.g., Add more examples, faster loading..."
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 min-h-[80px]"
                data-testid="feedback-improve"
              />
            </div>

            <Button
              onClick={handleFeedbackSubmit}
              disabled={feedbackSubmitting || feedbackRating === 0}
              className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold"
              data-testid="submit-feedback-btn"
            >
              {feedbackSubmitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" /> Submit Feedback
                </span>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GSTRegistration;
